// Hex hover card: owner, controller, terrain, city. Follows state.hover.
//
//   ┌──────────────────────────────────────────────┐
//   │ ▮ Ukraine                                [src]│  owner the default worldview recognises
//   │ ⚑ Shown as Russia in this view · recognised   │  only where the leader's view differs
//   │   as Ukraine                       [official] │  (a claim, never stated as fact)
//   │ ⚔ Controlled by Russia · occupied             │  controller and how ("Part of the area held by" when partial,
//   │                                               │  "Control unknown · no observation until …" in a data gap)
//   │ ★ city · city of Israel   ▤ terrain …        │  the banner's city; whose, if not the owner's
//   └──────────────────────────────────────────────┘
//   No owner: "Open water" at sea, "Unclaimed" only on land that belongs to no state.
//
// The card is never dropped here on a camera move: the view re-picks under a resting pointer
// (view/picking.ts HoverTracker) and reports a change of hex, so a card kept over an unchanged hex
// is right and one dropped would not come back. The services drop it on a date jump, which
// leaves the pointer where it was: the card goes back up (restoredPick).

import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import type { City, DateStr } from '../core/schema.ts';
import type { HoverInfo } from '../services/api.ts';
import type { CONTROL, HexDetail, WorldSnapshot } from '../world/api.ts';
import { useServices } from './context.ts';
import { footprintOf, heldBasis, heldFrac, type Footprint } from './extensions.ts';
import { MODELLED_NOTE, modelledTag } from './format.ts';
import { cityCountryNote, heldLabel, heldLead, heldNote, popFigure, popNote, restoredPick, shownAs, topCity, unownedName, type PickChange } from './hovercard.ts';
import { Icon } from './icons.tsx';
import { actorLook, lookOf, reportOf } from './nation.ts';
import { ClaimLabel, SourceChip } from './parts.tsx';
import { hudSize } from './store.ts';

const OFFSET = 18;
const EDGE = 8;

// Keyed by the CONTROL codes of src/world/api.ts (type-only import: ui never loads world code).
type ControlCode = (typeof CONTROL)[keyof typeof CONTROL];

const CONTROL_LABEL: Record<ControlCode, string> = {
  0: 'controlled',
  1: 'occupied',
  2: 'contested',
  3: 'annexation claimed',
  4: 'breakaway',
};

// Cities by hex (a hex can hold several: Hong Kong, Zhuhai and Tuen Mun share one), built once per snapshot.
const cityIndex = new WeakMap<WorldSnapshot, Map<number, City[]>>();

function cityAt(snap: WorldSnapshot, hex: number): City | undefined {
  let map = cityIndex.get(snap);
  if (!map) {
    map = new Map();
    for (const c of snap.cities) {
      map.set(c.hex, [...(map.get(c.hex) ?? []), c]);
    }
    cityIndex.set(snap, map);
  }
  return topCity(map.get(hex) ?? []);
}

interface Seen {
  pick: HoverInfo | null;
  date: DateStr;
  footprint: Footprint | null;
}

function changeSince(seen: Seen, footprint: Footprint | null, date: DateStr): PickChange {
  if (seen.footprint !== footprint) {
    return 'camera moved';
  }
  return seen.date === date ? 'none' : 'date jump';
}

export function HoverTooltip() {
  const { state, world, actions } = useServices();
  const ref = useRef<HTMLDivElement>(null);
  const hover = state.hover.value;
  const snap = state.snapshot.value;
  const scale = hudSize.value.scale;
  const date = state.date.value;
  const footprint = footprintOf(state);
  const last = useRef<Seen>({ pick: hover, date, footprint });

  // A date jump cleared the card under a resting pointer: bring it back (it reads the new date).
  // Skipped when a newer pick already arrived (the view reports first) or the camera moved.
  useEffect(() => {
    const back = restoredPick(last.current.pick, hover, changeSince(last.current, footprint, date));
    last.current = { pick: hover, date, footprint };
    if (back && state.hover.peek() === null) {
      actions.hover(back);
    }
  }, [hover, date, footprint]);

  // Keep the card inside the HUD: flip left/up near the right/bottom edges.
  useLayoutEffect(() => {
    const el = ref.current;
    const root = el?.offsetParent as HTMLElement | null;
    if (!el || !root || !hover) {
      return;
    }
    // Picks are in map CSS pixels; the HUD may be zoomed, so convert to its units.
    const px = hover.x / scale;
    const py = hover.y / scale;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let x = px + OFFSET;
    let y = py + OFFSET;
    if (x + w > root.clientWidth - EDGE) {
      x = px - w - OFFSET;
    }
    if (y + h > root.clientHeight - EDGE) {
      y = py - h - OFFSET;
    }
    el.style.transform = `translate(${Math.max(EDGE, x)}px, ${Math.max(EDGE, y)}px)`;
  }, [hover?.x, hover?.y, hover?.hex, scale]);

  if (!hover || !snap) {
    return null;
  }

  const hex = hover.hex;
  const terrain = safeHexInfo(world.hexInfo.bind(world), hex);
  const city = cityAt(snap, hex);
  const iso3 = world.countryAtHex(hex);
  const report = iso3 ? reportOf(world, iso3) : null;

  // The owner the default worldview recognises; a leader's view may draw the hex as another's.
  const shown = actorLook(world, snap.owner[hex]);
  const recognisedIndex = snap.claim?.[hex] ?? 0;
  const recognised = recognisedIndex !== 0 ? actorLook(world, recognisedIndex) : shown;

  // Held by whom: hexInfo speaks for the default worldview; the snapshot is the fallback.
  const ctrlIndex = snap.controller[hex];
  const heldBy = terrain ? terrain.controller : (actorLook(world, ctrlIndex)?.name ?? null);
  const claim =
    recognisedIndex !== 0
      ? shownAs(shown?.name ?? null, recognised?.name ?? null, { sources: terrain?.sources ?? [], heldBy })
      : null;
  const how = terrain ? heldLabel(terrain.control) : (CONTROL_LABEL[snap.control[hex] as ControlCode] ?? null);
  const frac = terrain ? heldFrac(terrain) : snap.controlFrac[hex];
  const basis = terrain ? heldBasis(terrain) : null;
  const held = [how, heldNote(basis)].filter(Boolean).join(' · ');

  // The city is its own country's, even on a neighbour's hex or on water: say whose.
  const cityNote = city ? cityCountryNote(lookOf(world, city.iso3).name, recognised?.name ?? null) : null;
  const cityYear = city?.pop_year || undefined;

  return (
    <div ref={ref} class="wc-hover" role="tooltip">
      <div class="wc-hover-head">
        {recognised ? <span class="wc-swatch wc-swatch--lg" style={{ background: recognised.colors.primary }} /> : null}
        <span class="wc-hover-name">{recognised ? recognised.name : unownedName(terrain?.terrain ?? null)}</span>
        {report ? <SourceChip ids={report.country.sources} /> : null}
      </div>

      {claim ? (
        <div class="wc-hover-row wc-hover-claim">
          <Icon name="border" size={13} />
          <span>{claim}</span>
          <ClaimLabel kind="official" />
        </div>
      ) : null}

      {heldBy || basis === 'unknown' ? (
        <div class="wc-hover-row wc-hover-ctrl">
          <Icon name="control" size={13} />
          {heldLead(frac, basis)} {heldBy ? <b>{heldBy}</b> : null}
          {held ? <i>{heldBy ? ' · ' : ''}{held}</i> : null}
        </div>
      ) : null}

      {city ? (
        <div class="wc-hover-row">
          <Icon name={city.capital ? 'star' : 'population'} size={13} />
          <b>{city.name}</b>
          {cityNote ? <span class="wc-year">{cityNote}</span> : null}
          {popFigure(city.pop) ? (
            <span class="wc-num" title={modelledTag(cityYear) ? MODELLED_NOTE : undefined}>{popFigure(city.pop)}</span>
          ) : null}
          {popNote(city.pop, city.pop_year) ? <span class="wc-year">{popNote(city.pop, city.pop_year)}</span> : null}
          <SourceChip ids={city.sources} year={cityYear} tag={modelledTag(cityYear)} />
        </div>
      ) : null}

      {terrain ? (
        <div class="wc-hover-row wc-hover-terrain">
          <Icon name="map" size={13} />
          {[terrain.terrain, terrain.hill, terrain.feature].filter(Boolean).join(' · ')}
          {terrain.river === 'none' ? '' : ` · ${terrain.river === 'major' ? 'major river' : 'river'}`}
          {terrain.elevM > 0 ? <span class="wc-year"> {terrain.elevM} m</span> : null}
          <SourceChip ids={terrain.sources} />
        </div>
      ) : null}
    </div>
  );
}

// Terrain facts are optional in the card: a failing look-up hides the row, never the card.
function safeHexInfo(hexInfo: (hex: number) => HexDetail, hex: number): HexDetail | null {
  try {
    return hexInfo(hex);
  } catch {
    return null;
  }
}
